import Flickity from "flickity";
import carouselCellAccessibility from "./carousel-cell-accessibility";

/**
 * Product Filters Carousel
 * @param {object} opts
 */
function productsCarousel(opts) {
  const config = {
    dom: {
      wrapper: "js-products-carousel-wrapper",
      carousel: "js-products-carousel",
      carouselCell: "js-products-carousel-cell",
      pi: "js-pi",
      shopAll: "js-shop-all"
    },
    cls: {
      active: "is-active",
      fullHeight: "full-height",
      hide: "hide"
    }
  };
  const c = __.extend(config, opts);

  const flickityOpts = {
    contain: true,
    draggable: true,
    dragThreshold: 5,
    pageDots: true,
    prevNextButtons: true,
    wrapAround: true,
    cellAlign: "center",
    lazyload: true,
    accessibility: false
  };
  const flickityMobileOpts = {
    contain: true,
    draggable: true,
    dragThreshold: 5,
    pageDots: true,
    prevNextButtons: true,
    wrapAround: true,
    cellAlign: "center",
    lazyload: true,
    selectedAttraction: 0.4,
    friction: 0.8,
    accessibility: false
  };

  // global elements
  const { template } = SDG.Data || false;
  const isCartPage = template === "cart" || false;
  const $wrappers = document.body.querySelectorAll(`.${c.dom.wrapper}`);
  const flickityCarousels = [];
  const cellA11y = carouselCellAccessibility({ cellClass: c.dom.carouselCell });
  let view;

  /**
   * On Window Resize
   */
  function onWindowResize() {
    // desktop
    __.mq({
      view: "desktop",
      callback: () => {
        if (view !== "desktop") {
          view = "desktop";
          rebuildFlickity();
        }
      }
    });

    // mobile
    __.mq({
      view: "mobile",
      callback: () => {
        if (view !== "mobile") {
          view = "mobile";
          rebuildFlickity();
        }
      }
    });
  }

  function resizeFlickity(height = 0) {
    [...$wrappers].forEach(($wrapper, index) => {
      const activeCarousel = flickityCarousels[index];
      if (activeCarousel) { 
        const $viewport = activeCarousel.viewport;
        const $carousel = $wrapper.querySelector(`.${c.dom.carousel}`);
        const cells = $carousel?.querySelectorAll(`.${c.dom.carouselCell}`);
        
        if (!$viewport) return;
        
        const viewportHeight = $viewport.clientHeight;
        
        // If height is provided, use it
        if (height > 0) {
          if (viewportHeight < height) {
            $viewport.style.height = `${height}px`;
            activeCarousel.resize();
          }
        } else if (viewportHeight === 0 && cells && cells.length > 0) {
          // If viewport height is 0, calculate from cells
          const maxCellHeight = calculateMaxCellHeight(cells);
          if (maxCellHeight > 0) {
            $viewport.style.height = `${maxCellHeight}px`;
            activeCarousel.resize();
          }
        }

        cellA11y.sync(activeCarousel);
      }
    });
  }


  /**
   * Calculate maximum cell height
   */
  function calculateMaxCellHeight(cells) {
    const isMobile = view === "mobile";
    const padding = isMobile ? 21 : 0;
    let maxHeight = 0;
    [...cells].forEach(($cell) => {
      // Reset height temporarily to get natural height
      const originalHeight = $cell.style.height;
      $cell.style.height = "auto";
      const cellHeight = $cell.offsetHeight;
      $cell.style.height = originalHeight;

      if (cellHeight > maxHeight) {
        maxHeight = cellHeight;
      }
    });
    return maxHeight + padding;
  }

  /**
   * rebuild flickity
   */
  function rebuildFlickity(isEventDriven = false) {
    const carouselOpts = view === "mobile" ? flickityMobileOpts : flickityOpts;

    [...$wrappers].forEach(($wrapper, index) => {
      const activeCarousel = flickityCarousels[index];
      if (activeCarousel) activeCarousel.destroy();

      const $carousel = $wrapper.querySelector(`.${c.dom.carousel}`);
      const cells = $carousel.querySelectorAll(`.${c.dom.carouselCell}`);

      if (isEventDriven) {
        // reset carousel position to avoid shifting
        $carousel.style.left = 0;
      }

      [...cells].forEach(($item) => {
        __.removeClass($item, c.cls.fullHeight);
      });
      cellA11y.clear(cells);

      // only build carousel if there are more than 4 cells
      if (!$carousel || cells.length < 4) {
        const $shopAll = $wrapper.querySelector(`.${c.dom.shopAll}`);
        $carousel.style.opacity = 1;
        if ($shopAll) {__.addClass($shopAll, c.cls.hide);}
        return;
      }

      carouselOpts.on = {
        ready: () => {
          [...cells].forEach(($item) => {
            __.addClass($item, c.cls.fullHeight);
          });

          if (isEventDriven) {
            // reset carousel position to avoid shifting
            $carousel.style.removeProperty("left");
          }

          // Ensure viewport has correct height
          const newCarousel = flickityCarousels[index];
          if (newCarousel && newCarousel.viewport) {
            const viewportHeight = newCarousel.viewport.clientHeight;
            if (viewportHeight === 0) {
              const liveMaxCellHeight = calculateMaxCellHeight(cells);
              if (liveMaxCellHeight > 0) {
                newCarousel.viewport.style.height = `${liveMaxCellHeight}px`;
                newCarousel.resize();
              }
            }
            cellA11y.sync(newCarousel);
          }

          window.dispatchEvent(new CustomEvent("product-view-changed"));
        }
      };

      // // Set the offset divisor based on viewport size
      // let offsetDivisor;
      // if (view === 'desktop') {
      //   offsetDivisor = 1.33;
      // }

      // if (view === 'mobile') {
      //   offsetDivisor = 3;
      // }

      // const $flickityCell = $carousel.querySelector(`.${c.dom.carouselCell}`);
      // const leftOffset = setFlickityOffset($flickityCell.offsetWidth, offsetDivisor);
      // const carouselOffset = $carousel.offsetWidth;
      // const cellAlign = leftOffset / carouselOffset;
      // const updatedFlickityOpts = __.extend(flickityOpts, { cellAlign });

      setTimeout(() => {
        if (activeCarousel) {
          flickityCarousels[index] = new Flickity($carousel, carouselOpts);
        } else {
          flickityCarousels.push(new Flickity($carousel, carouselOpts));
        }

        // Force resize after initialization to ensure correct height
        const newCarousel = flickityCarousels[index];
        if (newCarousel) {
          cellA11y.bind(newCarousel);

          // Use requestAnimationFrame to ensure DOM is ready
          requestAnimationFrame(() => {
            if (newCarousel.viewport) {
              const viewportHeight = newCarousel.viewport.clientHeight;
              if (viewportHeight === 0) {
                const liveMaxCellHeight = calculateMaxCellHeight(cells);
                if (liveMaxCellHeight > 0) {
                  newCarousel.viewport.style.height = `${liveMaxCellHeight}px`;
                  newCarousel.resize();
                }
              } else {
                // Ensure all cells have the same height
                newCarousel.resize();
              }
              cellA11y.sync(newCarousel);
            }
          });
        }
      }, 500); // short timeout for Flickity cell sizing
    });
  }

  // /**
  //  * Set the flickity carousel offset
  //  */
  // function setFlickityOffset(flickityCellOffsetWidth, flickityOffsetDivisor) {
  //   Flickity.Cell.prototype.setDefaultTarget = () => {
  //     const marginProperty = this.parent.originSide === 'left' ? 'marginLeft' : 'marginRight';
  //     this.target = this.x + this.size[marginProperty];
  //   };

  //   return flickityCellOffsetWidth / flickityOffsetDivisor;
  // }

  /**
   * Init
   */
  function init() {
    if (!$wrappers?.length) return;

    if (isCartPage) {
      // small timeout to allow for cart page to load
      setTimeout(() => {
        onWindowResize();
        __.windowResize(onWindowResize);
      }, 500);
    } else {
      onWindowResize();
      __.windowResize(onWindowResize);
    }

    document.removeEventListener("resizeFlickity", () => resizeFlickity());
    document.addEventListener("resizeFlickity", (event) => resizeFlickity(event.detail.height));
  }

  return init();
}

export default productsCarousel;
